import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Badge, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import Card from "../../../../components/card";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { telefonoBonito } from "../../../../utils/telefonoBonito";
import useEsTelefono from "../../../../utils/esTelefono";
import { horarioBonito } from "../../../../utils/horarioBonito";
import PaginationComponent from "../../../../components/pagination/pagination";
import SearchInputComponent from "../../../../components/searchInput/searchInput";
import { formatLocalDateTime } from "../../../../components/enrollment/format";
import { EnrollmentRequestDetailsDTO, EnrollmentRequestListDTO, EnrollmentRequestSummaryDTO, REQUEST_MODE, REQUEST_STATUS } from "../../../../models/EnrollmentHistoryDTO";
import RequestFormModal from "./requestFormModal";
import RequestDetailsModal from "./requestDetailsModal";
import S from "./strings";
import AccionesAlumno from "../../../../components/enrollment/accionesAlumno";
import { buildPages, errorText, estadoVariant, HORARIO_LISTA } from "./helpers";
import useEnlaceDirecto from "../../../../utils/enlaceDirecto";

/*
	Cuatro bandejas y ninguna más (Esteban, 2026-09-22): "Todas" no existe, y lo que Caja no
	aplicó ya no tiene bandeja propia — vuelve a Rechazadas marcado como rechazado por Caja.
*/
type TabKey = "submitted" | "returned" | "drafts" | "pendingPayment" | "enrolled";
const TAB_STATUSES: Record<TabKey, number[]> = {
	submitted: [REQUEST_STATUS.ENVIADA], returned: [REQUEST_STATUS.DEVUELTA], drafts: [REQUEST_STATUS.BORRADOR],
	pendingPayment: [], enrolled: []
};
const PAGE_SIZES = [20, 50, 100];

/**
 * "Solicitudes de matrícula": the shared inbox where Ventas drafts remote sign-ups and Admisiones reviews, returns or approves them.
 * Backend decides visibility (Ventas only sees its own) and returns the permissions used to show buttons.
 */
const EnrollmentRequestsController: React.FC = () => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	// v2 session campus is set at login; after a plain page refresh fall back to the legacy campus ref so the screen still works.
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	/*
		Manda el plantel de la BARRA (el que la persona ve), no el guardado en la sesión: los dos se
		desfasaban y Matrículas abría Monterrey estando en San Pedro Sula; el botón Finanzas llevaba
		el plantel equivocado y movía la barra (Esteban, 2026-09-24). El de la sesión es solo respaldo.
	*/
	const currentCampus: any = legacyCampus
		? { id: legacyCampus.id, countryId: legacyCampus.company?.country_id ?? (sessionCampus?.id === legacyCampus.id ? sessionCampus?.countryId : undefined) }
		: sessionCampus;
	const campusId: number | null = currentCampus?.id ?? null;

	const [tab, setTab] = useState<TabKey>("submitted");
	// Ya matriculados: el alumno existe y tiene estado de cuenta.
	const conAccesos = tab === "pendingPayment" || tab === "enrolled";
	const [onlyMine, setOnlyMine] = useState<boolean>(false);
	const [yaAjustadoOnlyMine, setYaAjustadoOnlyMine] = useState<boolean>(false);
	const [search, setSearch] = useState<string>("");
	const [appliedSearch, setAppliedSearch] = useState<string>("");
	const [perPage, setPerPage] = useState<number>(PAGE_SIZES[0]);
	const [page, setPage] = useState<number>(1);
	const [result, setResult] = useState<EnrollmentRequestListDTO | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [form, setForm] = useState<{ requestId: number | null; modeId: number } | null>(null);
	const [detailsId, setDetailsId] = useState<number | null>(null);
	//Enlace de un aviso de Cliq: abre la solicitud en su plantel (2026-09-25).
	useEnlaceDirecto("solicitud", (id) => setDetailsId(id), (t) => setError(t));
	const [refreshTick, setRefreshTick] = useState<number>(0);

	const permissions = result?.permissions;

	useEffect(() => {
		if (campusId === null) {
			return;
		}
		setLoading(true);
		setError(null);
		const criteria: any = { statusIds: TAB_STATUSES[tab], search: appliedSearch || undefined, assignedToMe: onlyMine || undefined,
			pendingPayment: tab === "pendingPayment" || undefined, enrolled: tab === "enrolled" || undefined };
		//Solo vale la respuesta de la última petición: al cambiar de plantel la del anterior puede llegar tarde (2026-09-25).
		let vigente = true;
		http.enrollmentRequestsService.list(campusId, criteria, perPage, page)
			.then((r) => { if (vigente) setResult(r.data); })
			.catch((e) => { if (vigente) setError(errorText(e)); })
			.finally(() => { if (vigente) setLoading(false); });
		return () => { vigente = false; };
	}, [campusId, tab, appliedSearch, onlyMine, perPage, page, refreshTick]);

	/*
		Quien revisa abre viendo solo lo suyo: su bandeja es lo que le toca, no la del plantel
		entero (Esteban, 2026-09-23). La supervisora no tiene asignaciones, así que a ella no.
	*/
	useEffect(() => {
		if (!permissions || yaAjustadoOnlyMine) {
			return;
		}
		setYaAjustadoOnlyMine(true);
		//La asesora de Admisiones ya solo recibe lo suyo desde el servidor (asignado, revisado, creado o
		//comentado por ella): el filtro le escondería su propio historial (Esteban, 2026-09-24).
		if (permissions.isRevisor && !permissions.isSupervisora && !permissions.isAsesoraAdmisiones) {
			setOnlyMine(true);
		}
	}, [permissions]);

	// Ventas no revisa nada: si su bandeja de "Por revisar" está vacía, se le abren sus borradores.
	useEffect(() => {
		if (permissions && permissions.isSupervisora && !permissions.isRevisor && tab === "submitted" && result && result.total === 0 && !appliedSearch) {
			setTab("drafts");
		}
	}, [permissions?.isSupervisora]);

	const refresh = () => setRefreshTick((t) => t + 1);
	const changeTab = (key: TabKey) => { setTab(key); setPage(1); };
	const pages = useMemo(() => result ? buildPages(result.currentPage, result.lastPage, (p) => setPage(p), movil) : [], [result, movil]);
	const itemCount = result ? {
		start: result.total === 0 ? 0 : (result.currentPage - 1) * result.perPage + 1,
		end: Math.min(result.currentPage * result.perPage, result.total),
		total: result.total
	} : { start: 0, end: 0, total: 0 };

	const onSaved = (details: EnrollmentRequestDetailsDTO, action: "saved" | "submitted" | "approved" | "resent") => {
		setForm(null);
		setNotice(action === "resent" ? S.resentToCaja : action === "approved" ? S.approved : action === "submitted" ? S.submitted : S.saved);
		refresh();
		setDetailsId(details.id);
	};

	const tabs: { key: TabKey; label: string }[] = [
		{ key: "submitted", label: S.tabs.submitted }, { key: "returned", label: S.tabs.returned },
		{ key: "drafts", label: S.tabs.drafts },
		{ key: "pendingPayment", label: S.tabs.pendingPayment }, { key: "enrolled", label: S.tabs.enrolled }
	];

	if (campusId === null) {
		return <React.Fragment />;
	}

	return (
		<div className="container-fluid waModulo" style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
				<h4 style={{ margin: 0 }}>{S.title}</h4>
				<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
					{permissions?.canCreate ? (
						permissions.canPresencial ? (
							<Dropdown>
								<Dropdown.Toggle variant="primary" className="waBotonGrande"><FontAwesomeIcon icon={solid("plus")} />&nbsp;{S.newRequest}</Dropdown.Toggle>
								<Dropdown.Menu>
									<Dropdown.Item onClick={() => setForm({ requestId: null, modeId: REQUEST_MODE.PRESENCIAL })}>{S.newPresencial}</Dropdown.Item>
									<Dropdown.Item onClick={() => setForm({ requestId: null, modeId: REQUEST_MODE.REMOTA })}>{S.newRemote}</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						) : (
							<button type="button" className="btn btn-primary waBotonGrande" onClick={() => setForm({ requestId: null, modeId: REQUEST_MODE.REMOTA })}>
								<FontAwesomeIcon icon={solid("plus")} />&nbsp;{S.newRequest}
							</button>
						)
					) : ""}
				</div>
			</div>
			{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : ""}
			{notice ? <div className="alert alert-success" style={{ fontSize: 15, display: "flex", justifyContent: "space-between" }}>{notice}<button type="button" className="btn-close" style={{ fontSize: 10 }} onClick={() => setNotice(null)} /></div> : ""}

			<Card>
				<div style={{ padding: movil ? "12px 12px 0 12px" : "14px 18px 4px 18px" }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
						<div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
							<ul className="nav nav-pills waTabs" style={{ gap: 4 }}>
								{tabs.map((t) => (
									<li className="nav-item" key={t.key}>
										<button type="button" className={"nav-link btn btn-sm " + (tab === t.key ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => changeTab(t.key)}>{t.label}</button>
									</li>
								))}
							</ul>
							{permissions?.isRevisor && !permissions?.isAsesoraAdmisiones ? (
								<label style={{ position: "static", fontSize: 15, cursor: "pointer", margin: 0, whiteSpace: "nowrap" }}>
									<input type="checkbox" checked={onlyMine} onChange={(e) => { setOnlyMine(e.target.checked); setPage(1); }} />&nbsp;{S.onlyMine}
								</label>
							) : ""}
						</div>
						<div style={movil ? { flex: "1 1 100%", position: "relative" } : { minWidth: 320, position: "relative" }}>
							<SearchInputComponent search={search} onChangeSearch={setSearch}
								onClickSearch={() => { setAppliedSearch(search.trim()); setPage(1); }}
								onClearSearch={() => { setSearch(""); setAppliedSearch(""); setPage(1); }} />
						</div>
					</div>
					<div style={{ overflowX: "auto", marginTop: 18 }}>
						{loading && !result ? <div className="loader" /> : movil ? (
							/*
								En teléfono la tabla de diez columnas obliga a arrastrar de lado, y ellas llenan
								esto desde el celular (Esteban, 2026-09-22). Misma información, en tarjetas.
							*/
							<div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
								{!result || result.data.length < 1 ? (
									<div style={{ color: "var(--wa-apagado)", textAlign: "center", padding: 24 }}>{S.empty}</div>
								) : result.data.map((r: EnrollmentRequestSummaryDTO) => (
									<div key={"rqm_" + r.id} onClick={() => setDetailsId(r.id)}
										style={{ border: "1px solid var(--wa-filo)", borderRadius: 8, padding: "10px 12px", cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
										<div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
											<b style={{ fontSize: 15 }}>{r.studentName || "-"}</b>
											<Badge bg={estadoVariant(r.statusId, r.status)} style={{ fontWeight: 500, flexShrink: 0 }}>{r.status}</Badge>
										</div>
										{r.returnedByCaja ? <div className="waOroTexto" style={{ fontSize: 13, fontWeight: 700 }}>{S.returnedByCaja}</div> : ""}
										<div style={{ fontSize: 13, marginTop: 2 }}>{cursoCorto(r.courseName) || "-"}</div>
										<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
											{horarioBonito(r.scheduleName, HORARIO_LISTA) || "-"}
											{(r.scheduleCount || 1) > 1 ? ` · +${(r.scheduleCount || 1) - 1} ${(r.scheduleCount || 1) > 2 ? "cursos más" : "curso más"}` : ""}
										</div>
										{(r.advisorsShort || []).length > 0 ? (
											<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{S.col.advisors}: {(r.advisorsShort || []).join(", ")}</div>
										) : ""}
										<div style={{ fontSize: 13, color: "var(--wa-apagado)", marginTop: 4 }}>
											{S.col.id}{r.id} · {r.mode} · {r.isNewStudent ? "Nuevo" : "Registrado"}
											{r.phone ? " · " + telefonoBonito(r.phone, currentCampus?.countryId) : ""}
										</div>
										<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
											{/* Quién la mandó, quién de Admisiones la tiene y quién de Caja, para saber a quién escribirle (2026-09-25). */}
											{S.col.createdBy}: {r.createdByShort || r.createdBy || "-"} · {S.col.assignedTo}: {r.assignedToShort || r.assignedTo || "-"}
											{r.cajaShort ? <> · {S.col.caja}: {r.cajaShort}</> : ""} · {formatLocalDateTime(r.updatedAt)}
										</div>
										{conAccesos && r.studentId ? <div style={{ marginTop: 8 }}><AccionesAlumno studentId={r.studentId} campusId={campusId} alinear="inicio" /></div> : ""}
									</div>
								))}
							</div>
						) : (
							<table className="table table-hover" style={{ fontSize: 15, marginBottom: 0 }}>
								<thead>
									<tr>
										<th style={{ width: 52, textAlign: "center" }}>{S.col.id}</th>
										<th style={{ width: 230 }}>{S.col.student}</th>
										<th style={{ width: 250 }}>{S.col.course}</th>
										<th style={{ width: 140 }}>{S.col.advisors}</th>
										<th style={{ width: 92 }}>{S.col.mode}</th>
										{/* 1% + sin partir = tan ancha como la etiqueta más larga de la página, ni un pixel más. */}
										<th style={{ width: "1%", whiteSpace: "nowrap" }}>{S.col.status}</th>
										<th style={{ width: 130 }}>{S.col.createdBy}</th>
										<th style={{ width: 130 }}>{S.col.assignedTo}</th>
										<th style={{ width: 120 }}>{S.col.caja}</th>
										<th style={{ width: 128 }}>{S.col.updatedAt}</th>
										{/*
											Única columna sin ancho: se queda con el sobrante de la pantalla, así ninguna otra
											se estira (Curso / Horario y luego Estado salían enormes). En Pendiente Pago y
											Finalizadas lleva Ver perfil y Finanzas, como en Estudiantes (Esteban, 2026-09-23).
										*/}
										<th style={{ paddingRight: 18 }} />
									</tr>
								</thead>
								<tbody>
									{!result || result.data.length < 1 ? (
										<tr><td colSpan={11} style={{ color: "var(--wa-apagado)", textAlign: "center", padding: 24 }}>{S.empty}</td></tr>
									) : result.data.map((r: EnrollmentRequestSummaryDTO) => (
										<tr key={"rq_" + r.id} style={{ cursor: "pointer", opacity: loading ? 0.6 : 1 }} onClick={() => setDetailsId(r.id)}>
											<td style={{ whiteSpace: "nowrap", textAlign: "center" }}>#{r.id}</td>
											<td>
												<b>{r.studentName || "-"}</b>
												<div style={{ fontSize: 13, color: "var(--wa-apagado)", whiteSpace: "nowrap" }}>{r.isNewStudent ? "Nuevo" : "Registrado"}{r.phone ? ` · ${telefonoBonito(r.phone, currentCampus?.countryId)}` : ""}</div>
											</td>
											<td>
												{cursoCorto(r.courseName) || "-"}
												<div style={{ fontSize: 13, color: "var(--wa-apagado)", whiteSpace: "nowrap" }}>{horarioBonito(r.scheduleName, HORARIO_LISTA) || "-"}</div>
												{(r.scheduleCount || 1) > 1 ? (
													<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
														+{(r.scheduleCount || 1) - 1} {(r.scheduleCount || 1) > 2 ? "cursos más" : "curso más"}
													</div>
												) : ""}
											</td>
											{/* Una asesora por renglón: dos nombres juntos hacían la columna el doble de ancha. */}
											<td>{(r.advisorsShort || []).length > 0
												? (r.advisorsShort || []).map((nombre, i) => <div key={"as_" + i} style={{ whiteSpace: "nowrap" }}>{nombre}</div>)
												: "-"}</td>
											<td style={{ whiteSpace: "nowrap" }}>{r.mode}</td>
											<td style={{ whiteSpace: "nowrap" }}>
												<Badge bg={estadoVariant(r.statusId, r.status)} style={{ fontWeight: 500 }}>{r.status}</Badge>
												{r.returnedByCaja ? <div className="waOroTexto" style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{S.returnedByCaja}</div> : ""}
											</td>
											<td style={{ whiteSpace: "nowrap" }}>{r.createdByShort || r.createdBy || "-"}</td>
											<td style={{ whiteSpace: "nowrap" }}>{r.assignedToShort || r.assignedTo || "-"}</td>
											<td style={{ whiteSpace: "nowrap" }}>{r.cajaShort || "-"}</td>
											<td style={{ whiteSpace: "nowrap" }}>{formatLocalDateTime(r.updatedAt)}</td>
											<td className="celdaAcciones" style={{ paddingRight: 18 }}>
												{conAccesos && r.studentId ? <AccionesAlumno studentId={r.studentId} campusId={campusId} /> : ""}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>
			</Card>
			<div style={{ marginTop: 10 }}>
				<PaginationComponent pages={pages} pageSizes={PAGE_SIZES} itemCount={itemCount} onPageSizeChange={(size) => { setPerPage(size); setPage(1); }} />
			</div>

			{form ? (
				<RequestFormModal campusId={campusId} countryId={currentCampus.countryId} requestId={form.requestId} initialModeId={form.modeId}
					canPresencial={!!permissions?.canPresencial} esAdmisiones={!!permissions?.isRevisor} onClose={() => setForm(null)} onSaved={onSaved} />
			) : ""}
			{detailsId !== null && !form ? (
				<RequestDetailsModal campusId={campusId} countryId={currentCampus?.countryId} requestId={detailsId} onClose={() => setDetailsId(null)} onChanged={refresh}
					onEdit={(id) => { setDetailsId(null); setForm({ requestId: id, modeId: REQUEST_MODE.REMOTA }); }} />
			) : ""}
		</div>
	);
};

export default EnrollmentRequestsController;
